import { Fetcher } from '@this/src/util';

// 在庫確認ステータス（バックエンドと共通の文字列）
export type InventoryStatus =
  | 'available'
  | 'unavailable'
  | 'operator_check'
  | 'price_increased'
  | 'price_decreased'
  | 'ticket_type_changed'
  | 'ticket_type_changed_confirmed';

// ------------------------------------------------------------
// 開始API: POST /domestic_air/same_day_inventory_checks
// ------------------------------------------------------------
export type StartInventoryCheckResponseArgs = {
  check_id: string | null;
  target: boolean;
  item_indexes: number[];
};

export type StartInventoryCheckArgs = {
  checkId: string | null;
  target: boolean;
  itemIndexes: number[];
};

export type StartInventoryCheckSubmitArgs = {
  search_query_id: number;
};

export const convertStartInventoryCheckResponseToArgs = (
  response: StartInventoryCheckResponseArgs
): StartInventoryCheckArgs => ({
  checkId: response.check_id,
  target: response.target,
  itemIndexes: response.item_indexes
});

// ------------------------------------------------------------
// 結果取得API: GET /domestic_air/same_day_inventory_checks/:check_id
// ------------------------------------------------------------
export type InventoryCandidateResponseArgs = {
  seat_type: string;
  ticket_category: string;
  ticket_type: string;
  flight_number: string;
  price: number;
  price_index: number;
};

export type InventoryCandidateArgs = {
  seatType: string;
  ticketCategory: string;
  ticketType: string;
  flightNumber: string;
  price: number;
  priceIndex: number;
};

export type InventoryResultResponseArgs = {
  item_index: number;
  inventory_status: InventoryStatus;
  original_price: number | null;
  new_price: number | null;
  price_diff: number | null;
  candidates: InventoryCandidateResponseArgs[];
};

export type InventoryResultArgs = {
  itemIndex: number;
  inventoryStatus: InventoryStatus;
  originalPrice: number | null;
  newPrice: number | null;
  priceDiff: number | null;
  candidates: InventoryCandidateArgs[];
};

export type PollInventoryCheckResponseArgs =
  | { status: 'in_progress' }
  | { status: 'stale' }
  | { status: 'completed'; check_id: string; results: InventoryResultResponseArgs[] };

export type PollInventoryCheckArgs =
  | { status: 'in_progress' }
  | { status: 'stale' }
  | { status: 'completed'; checkId: string; results: InventoryResultArgs[] };

export const convertInventoryCandidateResponseToArgs = (
  response: InventoryCandidateResponseArgs
): InventoryCandidateArgs => ({
  seatType: response.seat_type,
  ticketCategory: response.ticket_category,
  ticketType: response.ticket_type,
  flightNumber: response.flight_number,
  price: response.price,
  priceIndex: response.price_index
});

export const convertInventoryResultResponseToArgs = (
  response: InventoryResultResponseArgs
): InventoryResultArgs => ({
  itemIndex: response.item_index,
  inventoryStatus: response.inventory_status,
  originalPrice: response.original_price,
  newPrice: response.new_price,
  priceDiff: response.price_diff,
  candidates: response.candidates.map(convertInventoryCandidateResponseToArgs)
});

export const convertPollInventoryCheckResponseToArgs = (
  response: PollInventoryCheckResponseArgs
): PollInventoryCheckArgs => {
  if (response.status === 'completed') {
    return {
      status: 'completed',
      checkId: response.check_id,
      results: response.results.map(convertInventoryResultResponseToArgs)
    };
  }
  return { status: response.status };
};

// ------------------------------------------------------------
// 確定API: POST /domestic_air/same_day_inventory_checks/:check_id/confirm
// ------------------------------------------------------------
export type ConfirmSelectionSubmitArgs = {
  item_index: number;
  price_index: number;
};

export type ConfirmInventoryCheckSubmitArgs = {
  search_query_id: number;
  selections?: ConfirmSelectionSubmitArgs[];
};

export type ConfirmInventoryResultResponseArgs = {
  item_index: number;
  inventory_status: InventoryStatus;
  new_price: number | null;
  price_index: number;
};

export type ConfirmInventoryResultArgs = {
  itemIndex: number;
  inventoryStatus: InventoryStatus;
  newPrice: number | null;
  priceIndex: number;
};

export type ConfirmInventoryCheckResponseArgs =
  | { status: 'confirmed'; check_id: string; results: ConfirmInventoryResultResponseArgs[] }
  | { status: 'stale' }
  | { status: 'candidate_not_found' };

export type ConfirmInventoryCheckArgs =
  | { status: 'confirmed'; checkId: string; results: ConfirmInventoryResultArgs[] }
  | { status: 'stale' }
  | { status: 'candidate_not_found' };

export const convertConfirmInventoryResultResponseToArgs = (
  response: ConfirmInventoryResultResponseArgs
): ConfirmInventoryResultArgs => ({
  itemIndex: response.item_index,
  inventoryStatus: response.inventory_status,
  newPrice: response.new_price,
  priceIndex: response.price_index
});

export const convertConfirmInventoryCheckResponseToArgs = (
  response: ConfirmInventoryCheckResponseArgs
): ConfirmInventoryCheckArgs => {
  if (response.status === 'confirmed') {
    return {
      status: 'confirmed',
      checkId: response.check_id,
      results: response.results.map(convertConfirmInventoryResultResponseToArgs)
    };
  }
  return { status: response.status };
};

// ------------------------------------------------------------
// API呼び出し関数
// ------------------------------------------------------------
export const startSameDayInventoryCheck = async (
  params: StartInventoryCheckSubmitArgs
): Promise<StartInventoryCheckArgs> => {
  const response = await Fetcher.post<StartInventoryCheckResponseArgs>(
    '/domestic_air/same_day_inventory_checks',
    params
  );
  return convertStartInventoryCheckResponseToArgs(response);
};

export const fetchSameDayInventoryCheck = async (
  checkId: string,
  searchQueryId: number
): Promise<PollInventoryCheckArgs> => {
  const response = await Fetcher.get<PollInventoryCheckResponseArgs>(
    `/domestic_air/same_day_inventory_checks/${checkId}?search_query_id=${searchQueryId}`
  );
  return convertPollInventoryCheckResponseToArgs(response);
};

export const confirmSameDayInventoryCheck = async (
  checkId: string,
  params: ConfirmInventoryCheckSubmitArgs
): Promise<ConfirmInventoryCheckArgs> => {
  const response = await Fetcher.post<ConfirmInventoryCheckResponseArgs>(
    `/domestic_air/same_day_inventory_checks/${checkId}/confirm`,
    params
  );
  return convertConfirmInventoryCheckResponseToArgs(response);
};
